import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Card,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  Container,
} from 'react-bootstrap';
import { FileUp, Download, Receipt } from 'lucide-react';
import axios from 'axios';
import { db } from '../firebase/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Cloudinary-Konfiguration
const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dqng2dgmq/upload'; // Ersetze <cloud_name> mit Deinem Cloudinary-Namen
const UPLOAD_PRESET = 'PfadiInvetar'; // Ersetze 'your_upload_preset' mit Deinem Upload-Preset

// Hilfsfunktion, um geladene Daten rekursiv mit den Defaultwerten zu mergen
const mergeWithDefaults = (loaded, defaults) => {
  const result = { ...defaults };

  Object.keys(defaults).forEach((key) => {
    if (typeof defaults[key] === 'object' && defaults[key] !== null) {
      result[key] = mergeWithDefaults(loaded?.[key] || {}, defaults[key]);
    } else {
      result[key] = loaded?.[key] ?? defaults[key];
    }
  });
  return result;
};

const defaultBasisInfo = { lagerTage: 0, teilnehmer: 0, jsTeilnehmer: 0, leiter: 0 };
const defaultBudgetDaten = {
  ausgaben: {
    rekognoszierung: {
      reise: { budget: 0, effektiv: 0 },
      anderes: { budget: 0, effektiv: 0 },
    },
    unterkunft: {
      miete: { budget: 0, effektiv: 0 },
      nebenkosten: { budget: 0, effektiv: 0 },
      kurtaxe: { budget: 0, effektiv: 0 },
      anderes: { budget: 0, effektiv: 0 },
    },
    verpflegung: {
      proPersonTag: { budget: 0, effektiv: 0 },
      anderes: { budget: 0, effektiv: 0 },
    },
    transport: {
      kollektivBillett: { budget: 0, effektiv: 0 },
      sonderTransport: { budget: 0, effektiv: 0 },
      materialTransport: { budget: 0, effektiv: 0 },
      anderes: { budget: 0, effektiv: 0 },
    },
    programm: {
      eintritte: { budget: 0, effektiv: 0 },
      material: { budget: 0, effektiv: 0 },
      ausfluge: { budget: 0, effektiv: 0 },
      preise: { budget: 0, effektiv: 0 },
      ausruestung: { budget: 0, effektiv: 0 },
      anderes: { budget: 0, effektiv: 0 },
    },
  },
  einnahmen: {
    teilnehmerBeitrag: { betrag: 0, proPerson: 0 },
    leiterBeitrag: { betrag: 0, proPerson: 0 },
    jsBeitrag: { proTag: 16 },
    praeventionsBeitrag: { proTag: 0, fix: 0 },
    abteilungsBeitrag: { betrag: 0 },
    gemeindeBeitrag: { betrag: 0 },
    fundraising: { betrag: 0 },
    spenden: { betrag: 0 },
    anderes: { betrag: 0 },
  },
};

// Funktion zum Löschen eines Cloudinary-Assets (serverseitiger Endpoint erforderlich)
const deleteCloudinaryResource = async (publicId) => {
  try {
    const response = await fetch('/api/delete-cloudinary', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ publicId }),
    });
    return response.ok;
  } catch (error) {
    console.error("Cloudinary deletion failed", error);
    return false;
  }
};

// Basisinformation und Export-Komponente
const BasisInfoCard = ({ lagerName, basisInfo, setBasisInfo, handleExportPDF }) => (
  <Card className="mb-4">
    <Card.Body>
      <Form.Group>
        <Form.Label>Lagername</Form.Label>
        <Form.Control type="text" value={lagerName} readOnly />
        <Form.Text className="text-muted">
          Dieser Lagername stammt aus dem Lageradmin und dient als Dokument-ID.
        </Form.Text>
      </Form.Group>
      <Form.Group className="mt-3">
        <Form.Label>Lagertage</Form.Label>
        <Form.Control
          type="number"
          value={basisInfo.lagerTage}
          onChange={(e) =>
            setBasisInfo((prev) => ({
              ...prev,
              lagerTage: parseInt(e.target.value) || 0,
            }))
          }
        />
      </Form.Group>
      <div className="d-flex justify-content-end mt-3">
        <Button variant="outline-primary" className="d-flex align-items-center" onClick={handleExportPDF}>
          <Download size={16} className="me-2" />
          Exportieren
        </Button>
      </div>
    </Card.Body>
  </Card>
);

// Ausgaben-Komponente
const AusgabenCard = ({ budgetDaten, setBudgetDaten, setAusgewaehlteKategorie, setShowModal }) => {
  return (
    <Card className="mb-4">
      <Card.Header className="bg-light">
        <h3 className="h5 mb-0">Ausgaben</h3>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive className="mb-0">
          <thead>
            <tr className="bg-light">
              <th className="px-2 py-2">Position</th>
              <th className="px-2 py-2" style={{ width: '120px' }}>Budget</th>
              <th className="px-2 py-2" style={{ width: '120px' }}>Effektiv</th>
              <th className="px-2 py-2" style={{ width: '80px' }}>Belege</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(budgetDaten.ausgaben).map(([kategorie, posten]) => (
              <React.Fragment key={kategorie}>
                <tr className="bg-light">
                  <td colSpan={4} className="px-2 py-2">
                    <strong className="text-uppercase">{kategorie}</strong>
                  </td>
                </tr>
                {Object.entries(posten).map(([item, werte]) => (
                  <tr key={`${kategorie}-${item}`}>
                    <td className="px-2 py-2 text-capitalize">{item}</td>
                    <td className="px-2 py-2">
                      <Form.Control
                        type="number"
                        size="sm"
                        value={werte.budget || 0}
                        onChange={(e) => {
                          const newBudget = { ...budgetDaten };
                          newBudget.ausgaben[kategorie][item].budget = parseFloat(e.target.value) || 0;
                          setBudgetDaten(newBudget);
                        }}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Form.Control
                        type="number"
                        size="sm"
                        value={werte.effektiv ?? 0}
                        onChange={(e) => {
                          const newBudget = { ...budgetDaten };
                          newBudget.ausgaben[kategorie][item].effektiv = parseFloat(e.target.value) || 0;
                          setBudgetDaten(newBudget);
                        }}
                        className="bg-light"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => {
                          setAusgewaehlteKategorie(`${kategorie}.${item}`);
                          setShowModal(true);
                        }}
                      >
                        <Receipt size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// Einnahmen-Komponente
const EinnahmenCard = ({ budgetDaten, setBudgetDaten }) => {
  return (
    <Card className="mb-4">
      <Card.Header className="bg-light">
        <h3 className="h5 mb-0">Einnahmen</h3>
      </Card.Header>
      <Card.Body className="p-0">
        <Table responsive className="mb-0">
          <tbody>
            {Object.entries(budgetDaten.einnahmen).map(([kategorie, werte]) => (
              <tr key={kategorie}>
                <td className="px-2 py-2 text-capitalize">{kategorie}</td>
                <td className="px-2 py-2" style={{ width: '200px' }}>
                  {Object.entries(werte).map(([feld, wert]) =>
                    feld !== 'total' && (
                      <Form.Group key={feld} className="mb-2">
                        <Form.Label className="small mb-1 text-capitalize">
                          {feld}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={wert || 0}
                          onChange={(e) => {
                            const newBudget = { ...budgetDaten };
                            newBudget.einnahmen[kategorie][feld] = parseFloat(e.target.value) || 0;
                            setBudgetDaten(newBudget);
                          }}
                        />
                      </Form.Group>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// Zusammenfassungs-Komponente
const ZusammenfassungCard = ({ computedBudgetDaten }) => {
  return (
    <Card className="mb-4">
      <Card.Header className="bg-light">
        <h3 className="h5 mb-0">Zusammenfassung</h3>
      </Card.Header>
      <Card.Body>
        <Row className="mb-3">
          <Col xs={12} md={4}>
            <h4 className="h6 mb-2">Gesamteinnahmen</h4>
            <p className="mb-0">
              Budget: CHF {computedBudgetDaten.gesamtEinnahmen?.budget.toFixed(2) || '0.00'}
            </p>
            <p className="mb-0">
              Effektiv: CHF {computedBudgetDaten.gesamtEinnahmen?.effektiv.toFixed(2) || '0.00'}
            </p>
          </Col>
          <Col xs={12} md={4}>
            <h4 className="h6 mb-2">Gesamtausgaben</h4>
            <p className="mb-0">
              Budget: CHF {computedBudgetDaten.gesamtAusgaben?.budget.toFixed(2) || '0.00'}
            </p>
            <p className="mb-0">
              Effektiv: CHF {computedBudgetDaten.gesamtAusgaben?.effektiv.toFixed(2) || '0.00'}
            </p>
          </Col>
          <Col xs={12} md={4}>
            <h4 className="h6 mb-2">Bilanz</h4>
            <p className="mb-0">
              Budget: CHF {computedBudgetDaten.bilanz?.budget.toFixed(2) || '0.00'}
            </p>
            <p className="mb-0">
              Effektiv: CHF {computedBudgetDaten.bilanz?.effektiv.toFixed(2) || '0.00'}
            </p>
          </Col>
        </Row>
        <Alert variant={computedBudgetDaten.bilanz?.budget >= 0 ? 'success' : 'danger'}>
          <p className="h4 mb-0">
            Bilanz: CHF {computedBudgetDaten.bilanz?.budget.toFixed(2)} (Budget) / CHF{' '}
            {computedBudgetDaten.bilanz?.effektiv.toFixed(2)} (Effektiv)
          </p>
        </Alert>
      </Card.Body>
    </Card>
  );
};

// Modal für Beleg-Upload, Anzeige und Löschung
const BelegUploadModal = ({
  showModal,
  setShowModal,
  ausgewaehlteKategorie,
  belege,
  setBelege,
  budgetDaten,
  setBudgetDaten,
}) => {
  // Funktion zum Löschen eines Belegs inklusive Cloudinary-Löschung
  const handleDeleteBeleg = async (belegId, publicId) => {
    if (window.confirm('Möchtest Du diesen Beleg wirklich löschen?')) {
      if (publicId) {
        const deleted = await deleteCloudinaryResource(publicId);
        if (!deleted) {
          alert('Fehler beim Löschen des Cloudinary-Eintrags.');
          return;
        }
      }
      const updatedBelege = belege.filter((b) => b.id !== belegId);
      setBelege(updatedBelege);
    }
  };

  // Funktion für den Upload zu Cloudinary, erweitert um resource_type = "auto" (damit auch PDFs funktionieren)
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('resource_type', 'auto'); // Damit auch PDF und andere nicht-bildliche Formate akzeptiert werden
    try {
      const response = await axios.post(CLOUDINARY_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data; // Enthält secure_url, public_id etc.
    } catch (error) {
      console.error('Cloudinary Upload failed:', error);
      throw error;
    }
  };

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Beleg hochladen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const file = formData.get('datei');
            try {
              // Upload an Cloudinary
              const uploadResult = await uploadToCloudinary(file);
              const neuerBeleg = {
                id: Date.now().toString(),
                betrag: parseFloat(formData.get('betrag')),
                beschreibung: formData.get('beschreibung'),
                datum: formData.get('datum'),
                kategorie: ausgewaehlteKategorie,
                // Statt dem File-Objekt speichern wir die URL und publicId
                dateiUrl: uploadResult.secure_url,
                publicId: uploadResult.public_id,
              };

              setBelege((prev) => [...prev, neuerBeleg]);

              // Effektive Wertanpassung
              if (ausgewaehlteKategorie) {
                const [kategorie, item] = ausgewaehlteKategorie.split('.');
                const newBudget = { ...budgetDaten };
                newBudget.ausgaben[kategorie][item].effektiv =
                  (newBudget.ausgaben[kategorie][item].effektiv || 0) + neuerBeleg.betrag;
                setBudgetDaten(newBudget);
              }
            } catch (error) {
              alert('Upload fehlgeschlagen.');
            }
            setShowModal(false);
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label>Betrag (CHF)</Form.Label>
            <Form.Control name="betrag" type="number" step="0.05" required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Beschreibung</Form.Label>
            <Form.Control name="beschreibung" type="text" required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Datum</Form.Label>
            <Form.Control name="datum" type="date" required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Beleg (PDF/Bild)</Form.Label>
            <Form.Control name="datei" type="file" accept=".pdf,image/*" required />
          </Form.Group>
          <div className="d-flex justify-content-end">
            <Button type="submit" variant="primary" className="d-flex align-items-center">
              <FileUp size={16} className="me-2" />
              Hochladen
            </Button>
          </div>
        </Form>

        {belege.length > 0 && ausgewaehlteKategorie && (
          <div className="mt-4">
            <h4 className="h6 mb-3">Hochgeladene Belege</h4>
            {belege
              .filter((beleg) => beleg.kategorie === ausgewaehlteKategorie)
              .map((beleg) => (
                <Card key={beleg.id} className="mb-2">
                  <Card.Body className="py-2">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div>
                        <p className="mb-0 fw-bold">{beleg.beschreibung}</p>
                        <small className="text-muted">
                          {beleg.datum} - CHF {beleg.betrag.toFixed(2)}
                        </small>
                      </div>
                      <div className="d-flex align-items-center">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => {
                            if (beleg.dateiUrl) {
                              window.open(beleg.dateiUrl, '_blank');
                            } else {
                              alert('Die Datei ist nicht verfügbar.');
                            }
                          }}
                        >
                          Anzeigen
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger"
                          onClick={() => handleDeleteBeleg(beleg.id, beleg.publicId)}
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

// Hauptkomponente
const LagerBudget = ({ lagerId, lagerName, lagerTage: propLagerTage }) => {
  const [basisInfo, setBasisInfo] = useState({ ...defaultBasisInfo, lagerTage: propLagerTage });
  const [budgetDaten, setBudgetDaten] = useState(defaultBudgetDaten);
  const [belege, setBelege] = useState([]);
  const [ausgewaehlteKategorie, setAusgewaehlteKategorie] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Update bei Änderung des Prop-Werts
  useEffect(() => {
    setBasisInfo((prev) => ({ ...prev, lagerTage: propLagerTage }));
  }, [propLagerTage]);

  // Laden der Budget-Daten
  const loadBudget = useCallback(async () => {
    if (!lagerId) return;
    setIsLoading(true);
    try {
      const docRef = doc(db, 'lagerBudget', lagerId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Geladene Daten für', lagerName, data);
        setBasisInfo({
          ...data.basisInfo,
          lagerTage: propLagerTage,
        });
        setBudgetDaten(mergeWithDefaults(data.budgetDaten, defaultBudgetDaten));
        setBelege(data.belege || []);
      } else {
        console.log('Kein Budget für', lagerName, 'gefunden – Standardwerte werden genutzt.');
        setBasisInfo({ ...defaultBasisInfo, lagerTage: propLagerTage });
        setBudgetDaten(defaultBudgetDaten);
        setBelege([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Budgets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [lagerId, lagerName, propLagerTage]);

  useEffect(() => {
    loadBudget();
  }, [loadBudget]);

  // PDF-Export-Funktion
  const handleExportPDF = async () => {
    const input = document.getElementById('exportContainer');
    if (!input) {
      console.error("Export-Container nicht gefunden!");
      return;
    }
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${lagerName}_Budget.pdf`);
  };

  // Berechnung abgeleiteter Werte
  const computedBudgetDaten = useMemo(() => {
    const daten = { ...budgetDaten };
    const gesamtPersonen = basisInfo.teilnehmer + basisInfo.leiter;

    const verpflegungTotal =
      daten.ausgaben.verpflegung.proPersonTag.budget *
      gesamtPersonen *
      basisInfo.lagerTage;
    daten.ausgaben.verpflegung.total = {
      budget: verpflegungTotal,
      effektiv:
        daten.ausgaben.verpflegung.proPersonTag.effektiv *
        gesamtPersonen *
        basisInfo.lagerTage,
    };

    const jsTotal =
      daten.einnahmen.jsBeitrag.proTag *
      basisInfo.jsTeilnehmer *
      basisInfo.lagerTage;
    daten.einnahmen.jsBeitrag.total = jsTotal;

    const praeventionsTotal =
      daten.einnahmen.praeventionsBeitrag.proTag *
        basisInfo.teilnehmer *
        basisInfo.lagerTage +
      daten.einnahmen.praeventionsBeitrag.fix;
    daten.einnahmen.praeventionsBeitrag.total = praeventionsTotal;

    daten.einnahmen.teilnehmerBeitrag.total =
      daten.einnahmen.teilnehmerBeitrag.proPerson * basisInfo.teilnehmer;
    daten.einnahmen.leiterBeitrag.total =
      daten.einnahmen.leiterBeitrag.proPerson * basisInfo.leiter;

    const gesamtAusgaben = Object.values(daten.ausgaben).reduce(
      (summe, kategorie) => {
        const kategorieTotal = Object.values(kategorie).reduce(
          (katSumme, posten) => {
            if (posten.budget !== undefined) {
              return {
                budget: katSumme.budget + (parseFloat(posten.budget) || 0),
                effektiv: katSumme.effektiv + (parseFloat(posten.effektiv) || 0),
              };
            }
            return katSumme;
          },
          { budget: 0, effektiv: 0 }
        );
        return {
          budget: summe.budget + kategorieTotal.budget,
          effektiv: summe.effektiv + kategorieTotal.effektiv,
        };
      },
      { budget: 0, effektiv: 0 }
    );

    const gesamtEinnahmenBudget = Object.values(daten.einnahmen).reduce(
      (summe, posten) => {
        if (posten.total !== undefined) {
          return summe + posten.total;
        } else if (posten.betrag !== undefined) {
          return summe + (parseFloat(posten.betrag) || 0);
        }
        return summe;
      },
      0
    );

    daten.gesamtEinnahmen = {
      budget: gesamtEinnahmenBudget,
      effektiv: gesamtEinnahmenBudget,
    };

    daten.gesamtAusgaben = gesamtAusgaben;
    daten.bilanz = {
      budget: daten.gesamtEinnahmen.budget - gesamtAusgaben.budget,
      effektiv: daten.gesamtEinnahmen.effektiv - gesamtAusgaben.effektiv,
    };

    return daten;
  }, [basisInfo, budgetDaten]);

  // Automatisches Speichern (Debounce 1 Sekunde)
  const handleSave = useCallback(async () => {
    if (!lagerId) {
      console.warn('Keine Lager-ID vorhanden – Speichern nicht möglich.');
      return;
    }
    try {
      console.log('Automatisches Speichern für', lagerName);
      await setDoc(doc(db, 'lagerBudget', lagerId), {
        basisInfo,
        budgetDaten: computedBudgetDaten,
        belege, // Enthält dateiUrl und publicId von Cloudinary
        updatedAt: new Date(),
        lagerName,
      });
      console.log('Budget gespeichert für', lagerName);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
    }
  }, [basisInfo, computedBudgetDaten, belege, lagerId, lagerName]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSave();
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [basisInfo, budgetDaten, belege, handleSave]);

  if (isLoading) {
    return (
      <div className="p-4">
        <Spinner animation="border" role="status" className="me-2" />
        Lade Budget-Daten...
      </div>
    );
  }

  return (
    <Container fluid className="p-3">
      <div id="exportContainer">
        <BasisInfoCard
          lagerName={lagerName}
          basisInfo={basisInfo}
          setBasisInfo={setBasisInfo}
          handleExportPDF={handleExportPDF}
        />
        <Row>
          <Col xs={12} lg={6}>
            <AusgabenCard
              budgetDaten={budgetDaten}
              setBudgetDaten={setBudgetDaten}
              setAusgewaehlteKategorie={setAusgewaehlteKategorie}
              setShowModal={setShowModal}
            />
          </Col>
          <Col xs={12} lg={6}>
            <EinnahmenCard
              budgetDaten={budgetDaten}
              setBudgetDaten={setBudgetDaten}
            />
          </Col>
        </Row>
        <ZusammenfassungCard computedBudgetDaten={computedBudgetDaten} />
      </div>
      <BelegUploadModal
        showModal={showModal}
        setShowModal={setShowModal}
        ausgewaehlteKategorie={ausgewaehlteKategorie}
        belege={belege}
        setBelege={setBelege}
        budgetDaten={budgetDaten}
        setBudgetDaten={setBudgetDaten}
      />
    </Container>
  );
};

export default LagerBudget;